import { Avatar, MonoLabel } from '@/design-system';
import type { SessionMembership } from '@/shared/api';
import { cn } from '@/shared/lib';

import { authCopy, roleAtTenant, rolesLabel } from './copy';

export interface TenantPickerProps {
    memberships: SessionMembership[];
    /** De omgeving waar je nu in zit; krijgt een accentrand. */
    currentTenantUuid?: string | null;
    busyTenantUuid?: string | null;
    onPick: (membership: SessionMembership) => void;
    title?: string;
    description?: string;
    /** Een fout bij het wisselen; staat onder de keuzes. */
    error?: string | null;
    className?: string;
}

/**
 * Omgevingskeuze voor een gebruiker met meerdere lidmaatschappen.
 *
 * Herbruikbaar: hij staat op het inlogscherm én in het gebruikersblok van de
 * rail om later te wisselen. Eén optie hoort hier nooit te komen - dan kiest
 * de app zelf.
 */
export function TenantPicker({
    memberships,
    currentTenantUuid,
    busyTenantUuid,
    onPick,
    title = authCopy.tenantTitle,
    description = authCopy.tenantBody,
    error,
    className,
}: TenantPickerProps) {
    return (
        <div className={cn('flex flex-col gap-3', className)}>
            <div>
                <h2 className="m-0 font-[family-name:var(--font-display)] text-[26px] leading-[1.2] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                    {title}
                </h2>
                <p className="mt-[6px] mb-0 text-[14px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {description}
                </p>
            </div>

            <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {memberships.map((membership) => {
                    const { tenant } = membership;
                    const current = tenant.uuid === currentTenantUuid;

                    return (
                        <li key={tenant.uuid}>
                            <button
                                type="button"
                                className={cn(
                                    'flex w-full min-h-[var(--control-h-md)] cursor-pointer items-center gap-3 rounded-[9px] px-[14px] py-3 text-left',
                                    'font-[family-name:var(--font-body)]',
                                    'border border-[var(--border-default)] bg-[var(--surface-card)]',
                                    'hover:border-[var(--accent)]',
                                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
                                    'disabled:cursor-not-allowed disabled:opacity-60',
                                    current &&
                                        'border-[var(--accent-border)] bg-[var(--accent-tint)]',
                                )}
                                onClick={() => onPick(membership)}
                                aria-current={current || undefined}
                                disabled={Boolean(busyTenantUuid)}
                            >
                                {tenant.logoUrl ? (
                                    <img
                                        src={tenant.logoUrl}
                                        alt=""
                                        className="h-[34px] w-[34px] shrink-0 rounded-full border border-[var(--placeholder-border)] object-contain"
                                    />
                                ) : (
                                    <Avatar size={34} variant="placeholder" />
                                )}

                                <span className="min-w-0 flex-1">
                                    <span className="block text-[14px] leading-[1.3] font-medium text-[var(--text-primary)]">
                                        {tenant.name}
                                    </span>
                                    <span className="mt-[2px] block text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                                        {roleAtTenant(rolesLabel(membership.roles), tenant.name)}
                                    </span>
                                </span>

                                {busyTenantUuid === tenant.uuid ? (
                                    <MonoLabel size={10} tone="accent">
                                        {authCopy.tenantBusy}
                                    </MonoLabel>
                                ) : membership.isDefault ? (
                                    <MonoLabel size={10}>{authCopy.tenantDefault}</MonoLabel>
                                ) : null}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {error ? (
                <p role="alert" className="m-0 text-[13px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}

            <p className="m-0 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                {authCopy.tenantNotice}
            </p>
        </div>
    );
}
